import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { message } from 'antd'
import cn from 'classnames'
import { Button } from '@forwardflow/ui-v2'
import {
   CalendarCheckIcon,
   CheckCircle02Icon,
   ChevronRightIcon,
   ClockIcon,
   Message01Icon,
   PhoneIcon,
} from '@forwardflow/icons'
import {
   useGetAllLeadSources,
   useGetAllRecommendedCampaign,
} from '@forwardflow/features/campaign'
import CampaignService from '@forwardflow/services/CampaignService'
import useSetupProgress from '@forwardflow/hooks/customHooks/onboarding/useSetupProgress'
import { useA2PRegistrationModalStore } from '@forwardflow/store/modal'
import { A2PRegistrationModal } from '@forwardflow/modals/A2PRegistrationModal'
import { IAllLeadSources, IRecommendedCampaign } from '@forwardflow/types'
import { formatNumber } from '@forwardflow/helpers/utils/formatNumber.ts'

interface IGetStartedProps {
   isCalendarConnected: boolean
   onConnectCalendar: () => void
}

type TStepId = 'calls' | 'texts' | 'calendar'
type TStepState = 'done' | 'waiting' | 'current' | 'next'

/**
 * The dashboard home until the first campaign is live. One path, one
 * thing to do at a time: start calling, let texts join, put appointments
 * on the calendar. Choices are hidden behind a small link, never the
 * main event.
 */
export const GetStarted = ({ isCalendarConnected, onConnectCalendar }: IGetStartedProps) => {
   const navigate = useNavigate()
   const [starting, setStarting] = useState(false)
   const [choosing, setChoosing] = useState(false)
   const [chosen, setChosen] = useState<string | null>(null)

   const { data: recommended } = useGetAllRecommendedCampaign()
   const { data: sources, isLoading: sourcesLoading } = useGetAllLeadSources({
      page: 1,
      limit: 50,
      search: '',
   })
   const { data: progress } = useSetupProgress()
   const { openA2PRegistrationModal } = useA2PRegistrationModalStore()

   /* ---------- What we already decided for the user ---------- */

   const all = (sources || []) as IAllLeadSources[]
   const recs = (recommended || []) as unknown as IRecommendedCampaign[]
   const draft = all.find((s) => s.status === 'draft' && s.id)
   const bySize = useMemo(
      () =>
         all
            .filter((s) => s.leadSource)
            .sort((a, b) => (b.totalLeads || 0) - (a.totalLeads || 0)),
      [all]
   )
   const pickedTitle =
      chosen || recs[0]?.title || bySize[0]?.leadSource || null
   const picked = bySize.find((s) => s.leadSource === pickedTitle)
   const pickedLeads = picked?.totalLeads
   const isRecommended = recs.some((r) => r.title === pickedTitle)

   /* ---------- Step states ---------- */

   const a2p = progress?.onboardingProgress?.a2pRegistration?.status
   const smsOn = progress?.deploymentConfig?.channelConfig?.smsEnabled
   const textsState: TStepState =
      smsOn || a2p === 'completed' ? 'done' : a2p === 'in_progress' ? 'waiting' : 'next'
   const calendarState: TStepState = isCalendarConnected ? 'done' : 'next'

   // Calls is always the first job on this page: the page only shows while
   // no campaign is live. The other two line up behind it.
   const steps: { id: TStepId; label: string; state: TStepState }[] = [
      { id: 'calls', label: draft ? 'Finish your campaign' : 'Start calling', state: 'current' },
      { id: 'texts', label: 'Let texts join', state: textsState },
      { id: 'calendar', label: 'Appointments on your calendar', state: calendarState },
   ]
   const doneCount = steps.filter((s) => s.state === 'done' || s.state === 'waiting').length

   /* ---------- Actions ---------- */

   const startCalling = async () => {
      if (draft?.id) {
         navigate(
            `/campaign/${encodeURIComponent(draft.id)}/phone-number?campaignName=${draft.campaignName || ''}`
         )
         return
      }
      if (!pickedTitle) return
      try {
         setStarting(true)
         const created: any = await CampaignService.createActiveLeadSource(pickedTitle)
         if (created?.id) {
            navigate(
               `/campaign/${encodeURIComponent(created.id)}/phone-number?campaignName=${created.campaignName}`
            )
         }
      } catch (error) {
         console.error('Error creating campaign:', error)
         message.error("That didn't work. Give it another try.")
      } finally {
         setStarting(false)
      }
   }

   const bd = progress?.businessDetails
   const knownCount = [bd?.legalBusinessName, bd?.websiteUrl, bd?.email, bd?.phoneNumber, bd?.city]
      .filter(Boolean).length

   /* ---------- Render ---------- */

   const StepMark = ({ state, n }: { state: TStepState; n: number }) => {
      if (state === 'done')
         return <CheckCircle02Icon width={22} height={22} color="#00D492" />
      if (state === 'waiting')
         return <ClockIcon width={22} height={22} color="#ff8904" />
      return (
         <span
            className={cn(
               'flex items-center justify-center w-[22px] h-[22px] rounded-full text-[11px] font-semibold',
               state === 'current' ? 'bg-primary text-white' : 'bg-white/10 text-white/40'
            )}
         >
            {n}
         </span>
      )
   }

   return (
      <div className="rounded-xl border border-white/10 bg-primary/10 p-6 md:p-8">
         <div className="flex flex-col lg:flex-row gap-8">
            {/* The path */}
            <div className="lg:w-[300px] shrink-0">
               <p className="text-white/50 text-xs uppercase tracking-wide mb-1">
                  Your first week
               </p>
               <p className="text-white text-xl font-semibold mb-5">
                  {doneCount === 0
                     ? "Three things, and you're running."
                     : `${doneCount} of 3 done.`}
               </p>
               <ol className="flex flex-col gap-3">
                  {steps.map((s, i) => (
                     <li
                        key={s.id}
                        className={cn('flex items-center gap-3', {
                           'opacity-50': s.state === 'next',
                        })}
                     >
                        <StepMark state={s.state} n={i + 1} />
                        <span className={cn('text-sm', s.state === 'current' ? 'text-white font-medium' : 'text-white/80')}>
                           {s.label}
                        </span>
                        {s.state === 'waiting' && (
                           <span className="text-[#ff8904] text-xs">with the carriers</span>
                        )}
                     </li>
                  ))}
               </ol>
            </div>

            {/* The one thing to do now */}
            <div className="flex-1 rounded-xl bg-white/[0.05] p-6 md:p-7 flex flex-col gap-5">
               <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wide">
                  <PhoneIcon width={14} color="#A684FF" /> Step 1 · {draft ? 'Finish your campaign' : 'Start calling'}
               </div>

               {draft ? (
                  <>
                     <p className="text-white text-2xl font-semibold leading-tight">
                        You started a campaign on{' '}
                        <span className="text-[#dab2ff]">{draft.leadSource}</span>.
                        A minute more and it's live.
                     </p>
                     <p className="text-white/60 text-sm">
                        Your numbers and voice are in place. Review them and
                        press go.
                     </p>
                     <Button className="w-full md:w-auto md:px-8 self-start" onClick={startCalling}>
                        Finish and go live <ChevronRightIcon width={16} />
                     </Button>
                  </>
               ) : pickedTitle ? (
                  <>
                     <p className="text-white text-2xl font-semibold leading-tight">
                        We'll call the leads in{' '}
                        <span className="text-[#dab2ff]">{pickedTitle}</span>
                        {typeof pickedLeads === 'number' && pickedLeads > 0 && (
                           <span className="text-white/50 text-lg font-normal">
                              {' '}· {formatNumber(pickedLeads)} leads
                           </span>
                        )}
                        , and book the appointments for you.
                     </p>
                     <ul className="text-white/60 text-sm space-y-1.5">
                        <li>• Calls start today, from local numbers you already picked.</li>
                        <li>• Texts join on their own once the carriers approve your business.</li>
                        <li>• Booked appointments show up tagged in Follow Up Boss.</li>
                        {isRecommended && (
                           <li className="text-[#05df72]">• This is the source we recommend to begin with.</li>
                        )}
                     </ul>
                     <div className="flex flex-col md:flex-row md:items-center gap-3">
                        <Button className="md:px-8" onClick={startCalling} loading={starting}>
                           {starting ? 'Setting up' : 'Start calling'} <ChevronRightIcon width={16} />
                        </Button>
                        {!choosing && bySize.length > 1 && (
                           <button
                              type="button"
                              className="text-white/50 text-sm hover:text-white text-left"
                              onClick={() => setChoosing(true)}
                           >
                              Different leads? Choose another source
                           </button>
                        )}
                     </div>
                     {choosing && (
                        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                           <p className="text-white/50 text-xs mb-2">
                              Pick one. You can run more campaigns later.
                           </p>
                           <div className="flex flex-col gap-1 max-h-[220px] overflow-y-auto pr-1">
                              {bySize.map((s) => {
                                 const active = s.leadSource === pickedTitle
                                 return (
                                    <button
                                       key={s.leadSource}
                                       type="button"
                                       onClick={() => {
                                          setChosen(s.leadSource)
                                          setChoosing(false)
                                       }}
                                       className={cn(
                                          'flex items-center justify-between rounded-md px-3 py-2 text-sm text-left transition-colors',
                                          active
                                             ? 'bg-primary/30 text-white'
                                             : 'text-white/80 hover:bg-white/5'
                                       )}
                                    >
                                       <span className="truncate">{s.leadSource}</span>
                                       <span className="text-white/40 text-xs shrink-0 ml-3">
                                          {formatNumber(s.totalLeads || 0)} leads
                                       </span>
                                    </button>
                                 )
                              })}
                           </div>
                        </div>
                     )}
                  </>
               ) : sourcesLoading ? (
                  <>
                     <div className="h-8 w-3/4 rounded bg-white/10 animate-pulse" />
                     <div className="h-4 w-1/2 rounded bg-white/10 animate-pulse" />
                  </>
               ) : (
                  <>
                     <p className="text-white text-2xl font-semibold leading-tight">
                        We're still pulling your leads in from Follow Up Boss.
                     </p>
                     <p className="text-white/60 text-sm">
                        This usually takes under an hour. Come back then and
                        this button will be ready. Meanwhile, the two steps
                        below take a couple of minutes.
                     </p>
                     <Button className="self-start md:px-8" disabled>
                        Waiting for your leads
                     </Button>
                  </>
               )}
            </div>
         </div>

         {/* Up next, quietly */}
         <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className={cn('rounded-lg border border-white/10 bg-white/[0.03] p-4 flex items-start gap-3', { 'opacity-80': textsState === 'next' })}>
               <Message01Icon width={18} color="#A684FF" className="mt-0.5 shrink-0" />
               <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">
                     {textsState === 'done'
                        ? 'Texts are on'
                        : textsState === 'waiting'
                          ? 'Texts: waiting on the carriers'
                          : 'Step 2 · Let texts join your calls'}
                  </p>
                  <p className="text-white/50 text-xs mt-0.5">
                     {textsState === 'done'
                        ? 'Your campaigns text as well as call.'
                        : textsState === 'waiting'
                          ? 'Usually 3 to 5 days. Texts switch on by themselves. Nothing to do.'
                          : knownCount > 0
                            ? `Carriers need to know who you are. ${knownCount} of the details are already filled in; two minutes to confirm the rest.`
                            : 'Carriers need to know who you are. Two minutes, and texts join as soon as they approve.'}
                  </p>
                  {textsState === 'next' && (
                     <button
                        type="button"
                        className="mt-2 text-[#A684FF] text-sm font-medium hover:underline"
                        onClick={openA2PRegistrationModal}
                     >
                        Confirm your business details →
                     </button>
                  )}
               </div>
            </div>

            <div className={cn('rounded-lg border border-white/10 bg-white/[0.03] p-4 flex items-start gap-3', { 'opacity-80': calendarState === 'next' })}>
               <CalendarCheckIcon width={18} color="#A684FF" className="mt-0.5 shrink-0" />
               <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">
                     {calendarState === 'done'
                        ? 'Appointments land on your calendar'
                        : 'Step 3 · Appointments on your calendar'}
                  </p>
                  <p className="text-white/50 text-xs mt-0.5">
                     {calendarState === 'done'
                        ? 'Every booking shows up with the lead’s details.'
                        : 'Connect Google or Outlook once. Until then we still book and tag the lead for you.'}
                  </p>
                  {calendarState === 'next' && (
                     <button
                        type="button"
                        className="mt-2 text-[#A684FF] text-sm font-medium hover:underline"
                        onClick={onConnectCalendar}
                     >
                        Connect calendar →
                     </button>
                  )}
               </div>
            </div>
         </div>
         <A2PRegistrationModal />
      </div>
   )
}
